import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import {
  AlertTriangle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  FileDown,
} from "lucide-react";

import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const PAGE_SIZE = 25;

/* ----------------- Signed PDF generator (jsPDF) ----------------- */
function generateRedFlagPdf({ audit, quick_assessment: qa }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const M = 48;
  let y = M;

  // Brand header
  doc.setFont("times", "bold");
  doc.setFontSize(20);
  doc.setTextColor(212, 175, 55); // gold
  doc.text("ProsthoAI", M, y);
  doc.setFont("times", "normal");
  doc.setFontSize(9);
  doc.setTextColor(140, 140, 140);
  doc.text("Clinical Decision Support · Patent Pending US 64/062,147", M, y + 14);
  y += 30;

  // Divider
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(0.8);
  doc.line(M, y, PAGE_W - M, y);
  y += 22;

  // Document title
  doc.setFont("times", "bold");
  doc.setFontSize(16);
  doc.setTextColor(180, 20, 20);
  doc.text("Red Flag Warning — Acknowledgment Record", M, y);
  y += 20;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text(
    "Immutable evidence document generated from the ProsthoAI HIPAA audit ledger.",
    M,
    y
  );
  y += 26;

  // Acknowledgment block
  const kv = (label, value, dy = 14) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(140, 140, 140);
    doc.text(label.toUpperCase(), M, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text(String(value ?? "—"), M + 160, y);
    y += dy;
  };

  kv("Clinician email", audit.user_email);
  kv("Clinician ID", audit.user_id);
  kv("Acknowledged at", new Date(audit.ts).toLocaleString() + " (UTC iso: " + audit.ts + ")");
  kv("Client IP", audit.ip);
  kv("Audit row ID", audit.id);
  kv("Quick Assessment ID", qa?.id || "—");
  kv("Complexity rating", (qa?.complexity || "—").toUpperCase());
  kv(
    "Schema version",
    qa?.schema_version === "v1_legacy"
      ? "HISTORICAL v1 — NOT FOR CURRENT CLINICAL DECISION-MAKING"
      : (qa?.schema_version || "—"),
  );

  y += 6;
  doc.setDrawColor(220, 220, 220);
  doc.line(M, y, PAGE_W - M, y);
  y += 18;

  // Summary block
  if (qa?.summary) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(140, 140, 140);
    doc.text("AI CASE INTAKE SUMMARY", M, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    const lines = doc.splitTextToSize(qa.summary, PAGE_W - M * 2);
    doc.text(lines, M, y);
    y += lines.length * 13 + 10;
  }

  // Red flags
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(180, 20, 20);
  doc.text(
    `RED FLAGS DELIVERED TO CLINICIAN (${qa?.red_flags?.length || 0})`,
    M,
    y
  );
  y += 16;

  (qa?.red_flags || []).forEach((rf, i) => {
    if (y > PAGE_H - 120) {
      doc.addPage();
      y = M;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(180, 20, 20);
    doc.text(`${i + 1}.  [${(rf.urgency || "").toUpperCase()}]  ${rf.label}`, M, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    const detailLines = doc.splitTextToSize(rf.detail || "", PAGE_W - M * 2 - 18);
    doc.text(detailLines, M + 18, y);
    y += detailLines.length * 13 + 8;
  });

  // Recommendations
  if (qa?.recommend_complete_case_analysis || qa?.recommend_specialist) {
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(140, 140, 140);
    doc.text("PROSTHOAI RECOMMENDATIONS", M, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    if (qa.recommend_complete_case_analysis) {
      doc.text("· Upgrade to Complete Case Analysis recommended", M, y);
      y += 13;
    }
    if (qa.recommend_specialist) {
      doc.text("· Specialist referral recommended", M, y);
      y += 13;
    }
  }

  // Acknowledgment statement
  if (y > PAGE_H - 160) {
    doc.addPage();
    y = M;
  }
  y += 18;
  doc.setDrawColor(212, 175, 55);
  doc.line(M, y, PAGE_W - M, y);
  y += 18;
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  const ackText =
    `The clinician identified above (user ID ${audit.user_id}) was presented with the red ` +
    `flag warning shown in this document and explicitly tapped "I understand — show me the ` +
    `findings" on ${new Date(audit.ts).toLocaleString()}. This acknowledgment was written ` +
    `to the immutable ProsthoAI HIPAA audit ledger with audit row ID ${audit.id} and IP ` +
    `address ${audit.ip}. This record cannot be modified or deleted by any application path.`;
  const ackLines = doc.splitTextToSize(ackText, PAGE_W - M * 2);
  doc.text(ackLines, M, y);
  y += ackLines.length * 13 + 18;

  // Stamp
  doc.setFont("times", "bold");
  doc.setFontSize(10);
  doc.setTextColor(212, 175, 55);
  doc.text("ProsthoAI by Dr. Ronik Seecharan DMD, Prosthodontist", M, y);
  y += 12;
  doc.setFont("times", "italic");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(
    "30 years of clinical practice dedicated to treatment planning excellence",
    M,
    y
  );
  y += 12;

  // Footer
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text(
    "Generated " +
      new Date().toLocaleString() +
      " — Patent Pending US Application 64/062,147",
    M,
    PAGE_H - 24
  );

  const fname = `red-flag-${audit.id.slice(0, 8)}-${(audit.user_email || "user").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`;
  doc.save(fname);
}

export default function AdminRedFlags() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterEmail, setFilterEmail] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const load = async (nextOffset = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(nextOffset),
      });
      if (filterEmail.trim()) params.set("user_email", filterEmail.trim());
      if (filterFrom) params.set("date_from", filterFrom);
      if (filterTo) params.set("date_to", filterTo);
      const r = await api.get(`/admin/red-flags?${params.toString()}`);
      setItems(r.data.items);
      setTotal(r.data.total);
      setOffset(r.data.offset);
    } finally {
      setLoading(false);
    }
  };

  // Load once on mount. `load` is defined above and is used by both the
  // search and pagination handlers; adding it to the deps here would cause
  // this effect to re-fire on every filter keystroke (its closure reads
  // filterEmail/filterFrom/filterTo), triggering repeated API calls. The
  // mount-only behaviour is intentional.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(0); }, []);

  if (!user?.is_admin) {
    return (
      <Layout>
        <div className="text-[var(--pa-text-muted)]">Admin only.</div>
      </Layout>
    );
  }

  const onSearch = (e) => {
    e.preventDefault();
    load(0);
  };

  return (
    <Layout>
      <div className="flex items-start gap-3">
        <AlertTriangle size={22} className="text-red-400 mt-1" />
        <div>
          <div className="kicker text-red-300">Admin · Liability documentation</div>
          <h1
            data-testid="red-flags-title"
            className="font-serif text-3xl text-[var(--pa-text)]"
          >
            Red Flag Acknowledgment Ledger
          </h1>
          <p className="mt-2 text-xs text-[var(--pa-text-muted)] max-w-2xl leading-relaxed">
            Every Quick Assessment red-flag warning that was shown to a clinician AND the
            timestamp at which the clinician acknowledged it. Append-only.
          </p>
        </div>
        <Link
          to="/admin/audit-logs"
          data-testid="red-flags-audit-link"
          className="ml-auto text-xs text-[var(--pa-gold)] hover:underline"
        >
          ← Full audit logs
        </Link>
      </div>

      {/* Filters */}
      <form
        onSubmit={onSearch}
        data-testid="red-flags-filters"
        className="mt-7 flex flex-wrap items-end gap-3 p-4 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-md"
      >
        <div>
          <label className="kicker block mb-1">Clinician email contains</label>
          <input
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            data-testid="red-flags-filter-email"
            placeholder="dr.smith@…"
            className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2 w-60"
          />
        </div>
        <div>
          <label className="kicker block mb-1">From</label>
          <input
            type="date"
            value={filterFrom}
            onChange={(e) => setFilterFrom(e.target.value)}
            data-testid="red-flags-filter-from"
            className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2"
          />
        </div>
        <div>
          <label className="kicker block mb-1">To</label>
          <input
            type="date"
            value={filterTo}
            onChange={(e) => setFilterTo(e.target.value)}
            data-testid="red-flags-filter-to"
            className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-2"
          />
        </div>
        <button
          type="submit"
          data-testid="red-flags-filter-submit"
          className="bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-4 py-2 hover:bg-[var(--pa-gold-hover)] transition text-sm"
        >
          Search
        </button>
      </form>

      <div className="mt-3 text-[11px] text-[var(--pa-text-dim)]">
        {loading ? "Loading…" : `${total} total acknowledgment${total === 1 ? "" : "s"}`}
      </div>

      {/* List */}
      <div className="mt-4" data-testid="red-flags-list">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[var(--pa-text-muted)]">
            <Loader2 size={14} className="animate-spin text-[var(--pa-gold)]" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--pa-text-muted)] border border-dashed border-[var(--pa-border)] rounded-md">
            No red-flag acknowledgments yet.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((row, i) => {
              const a = row.audit;
              const qa = row.quick_assessment;
              return (
                <div
                  key={a.id}
                  data-testid={`red-flag-row-${a.id}`}
                  className="p-4 bg-[var(--pa-surface)] border border-red-500/30 rounded-md"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-xs text-[var(--pa-text)]">
                        <strong>{a.user_email}</strong>
                        <span className="text-[var(--pa-text-dim)] ml-2">{a.user_id}</span>
                      </div>
                      <div className="text-[11px] text-[var(--pa-text-dim)] mt-1">
                        Acknowledged {new Date(a.ts).toLocaleString()} · IP {a.ip}
                      </div>
                    </div>
                    {qa && (
                      <div className="text-right">
                        <div className="kicker text-red-300">
                          Complexity: <span className="text-[var(--pa-text)]">{qa.complexity}</span>
                        </div>
                        <div className="text-[10px] text-[var(--pa-text-dim)] mt-1">
                          {qa.image_count} photo{qa.image_count === 1 ? "" : "s"} · created{" "}
                          {qa.created_at ? new Date(qa.created_at).toLocaleString() : "—"}
                        </div>
                      </div>
                    )}
                  </div>

                  {qa?.schema_version === "v1_legacy" && (
                    <div
                      data-testid={`red-flag-legacy-badge-${a.id}`}
                      className="mt-3 inline-flex items-start gap-2 text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded border border-amber-500/50 bg-amber-500/10 text-amber-200"
                    >
                      <span className="font-semibold">Historical v1</span>
                      <span className="normal-case tracking-normal text-amber-100/90">
                        — not for current clinical decision-making
                      </span>
                    </div>
                  )}

                  {qa?.summary && (
                    <p
                      className="mt-3 text-xs text-[var(--pa-text-muted)] line-clamp-2"
                      data-testid={`red-flag-summary-${a.id}`}
                    >
                      {qa.summary}
                    </p>
                  )}

                  {qa?.red_flags?.length > 0 && (
                    <ul
                      className="mt-3 space-y-1.5"
                      data-testid={`red-flag-items-${a.id}`}
                    >
                      {qa.red_flags.map((rf, idx) => (
                        <li
                          key={idx}
                          className="text-xs bg-red-500/10 border border-red-500/30 rounded px-2.5 py-1.5 text-[var(--pa-text)]"
                        >
                          <span className="text-[10px] uppercase tracking-wider text-red-300 mr-1.5">
                            {rf.urgency}
                          </span>
                          <strong>{rf.label}</strong>
                          <div className="text-[var(--pa-text-muted)] mt-0.5 leading-relaxed">
                            {rf.detail}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-3 flex flex-wrap gap-2 text-[10px]">
                    {qa?.recommend_complete_case_analysis && (
                      <span className="px-2 py-0.5 rounded-full border border-[var(--pa-gold)]/50 text-[var(--pa-gold)]">
                        Recommended CCA
                      </span>
                    )}
                    {qa?.recommend_specialist && (
                      <span className="px-2 py-0.5 rounded-full border border-[var(--pa-gold)]/50 text-[var(--pa-gold)]">
                        Recommended specialist
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => generateRedFlagPdf(row)}
                      data-testid={`red-flag-pdf-${a.id}`}
                      className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-full border border-[var(--pa-gold)]/60 text-[var(--pa-gold)] hover:bg-[var(--pa-gold)]/10 transition text-[10px] uppercase tracking-wider"
                    >
                      <FileDown size={11} /> Evidence PDF
                    </button>
                    <span className="text-[var(--pa-text-dim)]">
                      Audit ID {a.id.slice(0, 8)}…
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="mt-6 flex items-center gap-3 justify-end" data-testid="red-flags-pagination">
          <button
            onClick={() => load(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
            data-testid="red-flags-prev"
            className="flex items-center gap-1 px-3 py-1.5 rounded border border-[var(--pa-border)] text-xs text-[var(--pa-text)] hover:border-[var(--pa-gold)] transition disabled:opacity-40"
          >
            <ChevronLeft size={12} /> Prev
          </button>
          <span className="text-[11px] text-[var(--pa-text-dim)]">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <button
            onClick={() => load(offset + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= total}
            data-testid="red-flags-next"
            className="flex items-center gap-1 px-3 py-1.5 rounded border border-[var(--pa-border)] text-xs text-[var(--pa-text)] hover:border-[var(--pa-gold)] transition disabled:opacity-40"
          >
            Next <ChevronRight size={12} />
          </button>
        </div>
      )}
    </Layout>
  );
}
